import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, BookOpen, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FEATURED_CALCULATORS, CATEGORIES, RELATED_ARTICLES, STATS } from '../data/mock';

const Home = () => {
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-saffron-50 via-white to-white border-b border-slate-100">
        <div className="absolute inset-0 hero-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 bg-white/80 border-saffron-200 text-saffron-700 px-3 py-1 gap-1">
              <Sparkles className="w-3 h-3" /> Free forever • No sign-up required
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-bharat-navy leading-tight">
              Every calculation you need,
              <span className="block mt-1">
                <span className="text-saffron-500">made</span>{' '}
                <span className="text-indgreen-600">for Bharat.</span>
              </span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
              From EMI and home loans in <span className="font-semibold text-bharat-navy">₹ INR</span> to BMI, age and scientific math — simple, fast and privacy-friendly. Made in India.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/calculator/basic">
                <Button className="bg-saffron-500 hover:bg-saffron-600 text-white h-11 px-6 shadow-sm">
                  Try basic calculator <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/calculator/emi">
                <Button variant="outline" className="h-11 px-6 border-slate-300 hover:bg-slate-50">
                  EMI Calculator →
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl">
              {STATS.map(s => (
                <div key={s.label} className="bg-white/70 backdrop-blur rounded-lg border border-slate-200/80 px-4 py-3">
                  <div className="font-display text-xl md:text-2xl font-bold text-bharat-navy">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Popular calculators */}
        <section className="mt-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-saffron-600 text-xs font-semibold uppercase tracking-wider mb-1">
                <TrendingUp className="w-3.5 h-3.5" /> Most Popular
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-bharat-navy">Popular Calculators</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {FEATURED_CALCULATORS.map((calc, idx) => {
              const Icon = calc.icon;
              const isSaffron = calc.color === 'saffron';
              return (
                <Link
                  key={calc.id}
                  to={`/calculator/${calc.id}`}
                  className="card-lift bg-white rounded-xl border border-slate-200 p-4 group"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    isSaffron ? 'bg-saffron-100 text-saffron-600' : 'bg-indgreen-100 text-indgreen-600'
                  }`}>
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display font-semibold text-sm text-bharat-navy group-hover:text-saffron-600 transition-colors">
                    {calc.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">{calc.desc}</p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Categories */}
        <section className="mt-14">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-bharat-navy mb-6">Browse by category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const isSaffron = cat.color === 'saffron';
              return (
                <div key={cat.name} className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSaffron ? 'bg-saffron-100 text-saffron-600' : 'bg-indgreen-100 text-indgreen-600'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-bharat-navy">{cat.name}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {cat.items.map(item => (
                      item.disabled ? (
                        <div key={item.id} className="flex items-center justify-between py-1.5 text-sm text-slate-400">
                          <span>{item.name}</span>
                          <span className="text-[9px] uppercase tracking-wider">Soon</span>
                        </div>
                      ) : (
                        <Link
                          key={item.id}
                          to={`/calculator/${item.id}`}
                          className="py-1.5 text-sm text-slate-700 hover:text-saffron-600 transition-colors border-b border-dashed border-transparent hover:border-saffron-300"
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Features strip */}
        <section className="mt-14 bg-gradient-to-r from-bharat-navy to-slate-800 rounded-2xl p-6 md:p-10 text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Lightning Fast', desc: 'Instant results, no loading screens, no waiting.' },
              { icon: Shield, title: 'Privacy First', desc: 'Everything runs in your browser. No tracking.' },
              { icon: BookOpen, title: 'Learn while you calculate', desc: 'Formulas, explanations, and Indian context included.' },
            ].map(f => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="flex gap-3">
                  <div className="w-11 h-11 rounded-lg bg-saffron-500/20 border border-saffron-400/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-saffron-300" />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold">{f.title}</h4>
                    <p className="text-sm text-slate-300 mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Articles */}
        <section className="mt-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-indgreen-600 text-xs font-semibold uppercase tracking-wider mb-1">
                <BookOpen className="w-3.5 h-3.5" /> Learn
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-bharat-navy">Helpful articles</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {RELATED_ARTICLES.map(a => (
              <article key={a.id} className="card-lift bg-white border border-slate-200 rounded-xl p-5 cursor-pointer">
                <Badge variant="secondary" className="bg-indgreen-50 text-indgreen-700 hover:bg-indgreen-100 text-[10px] uppercase tracking-wider">
                  {a.category}
                </Badge>
                <h3 className="font-display font-semibold text-bharat-navy mt-3 leading-snug">{a.title}</h3>
                <p className="text-xs text-slate-500 mt-2 line-clamp-3">{a.excerpt}</p>
                <p className="text-xs text-slate-400 mt-3">{a.readTime}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
